import React, { memo, use } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { PointClickEventObject } from 'highcharts';
import { buildExactMatchTrackedChange } from '@components-clean/core/filter-ui/action-bar/shared/filter.service';
import PanelFrame from '@components-clean/core/panel-frame';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { StackedChartData } from '@interfaces-clean/panels.interface';
import { RiskDashboardContext } from '@pages-clean/risk-assessment/risk-dashboard/context';
import {
    appendScreenFiltersQuery,
    stackedChartSeriesColours,
    stackedChartTooltipFormatter,
} from '@pages-clean/risk-assessment/risk-dashboard/sections/chart-utils';
import { PanelType, RiskEvaluationColor } from 'types/graphql-global-types';
import { AllPaths } from '@utils/router/all-paths';

const RiskDashboardCategories = () => {
    const {
        filteredRisks: risks,
        filterableRisks,
        categories,
        screenTrackedChanges,
        includeObsolete,
        filterVersion,
    } = use(RiskDashboardContext);
    const { t } = useTranslation();
    const navigate = useNavigate();

    const risksWithEvaluationHistory = risks.filter((risk) => !!risk.evaluationHistory?.length);
    const risksWithEvaluationHistoryAndCategory = risksWithEvaluationHistory.filter((risk) => !!risk.category);

    const categoryNames: (string | undefined)[] = [
        ...new Set(risksWithEvaluationHistoryAndCategory.map((risk) => risk.category!.name!)),
    ].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

    if (risksWithEvaluationHistoryAndCategory.length < risksWithEvaluationHistory.length) {
        categoryNames.unshift(undefined);
    }

    const redCounts: number[] = new Array(categoryNames.length).fill(0);
    const amberCounts: number[] = new Array(categoryNames.length).fill(0);
    const greenCounts: number[] = new Array(categoryNames.length).fill(0);

    let hasData = false;

    categoryNames.forEach((categoryName, index) => {
        let redCount = 0;
        let amberCount = 0;
        let greenCount = 0;

        risksWithEvaluationHistory.forEach((risk) => {
            if (risk?.category?.name! === categoryName) {
                switch (risk.score!.colour) {
                    case RiskEvaluationColor.RED:
                        redCount++;
                        break;
                    case RiskEvaluationColor.AMBER:
                        amberCount++;
                        break;
                    case RiskEvaluationColor.GREEN:
                        greenCount++;
                        break;
                    default:
                        break;
                }
            }
        });

        redCounts[index] = redCount;
        amberCounts[index] = amberCount;
        greenCounts[index] = greenCount;

        hasData ||= redCount > 0 || amberCount > 0 || greenCount > 0;
    });

    if (categoryNames[0] === undefined) {
        categoryNames[0] = 'Unassigned';
    }

    const chartData: StackedChartData = {
        xCategories: categoryNames as string[],
        series: hasData
            ? [
                  {
                      name: t('common:Red'),
                      values: redCounts,
                  },
                  {
                      name: t('common:Amber'),
                      values: amberCounts,
                  },
                  {
                      name: t('common:Green'),
                      values: greenCounts,
                  },
              ]
            : [],
        tooltipFormatter: stackedChartTooltipFormatter,
        hasScrollablePlotArea: true,
    };

    const handleClick = (event: PointClickEventObject) => {
        NavigationService.setViewRiskDetails();

        const categoryName = event.point.category as unknown as string;
        const matchValue = categoryName === 'Unassigned' ? null : categoryName;

        const categoryParent = categories.find((c) => c.items.some((item) => item.fieldKey === 'category'));
        const categoryItem = categoryParent?.items.find((item) => item.fieldKey === 'category');
        const ratingParent = categories.find((c) => c.items.some((item) => item.fieldKey === 'riskRating'));
        const ratingItem = ratingParent?.items.find((item) => item.fieldKey === 'riskRating');

        const clickTrackedChanges = [
            categoryItem && buildExactMatchTrackedChange(filterableRisks, categoryItem, matchValue, categoryParent!.id),
            ratingItem &&
                buildExactMatchTrackedChange(filterableRisks, ratingItem, event.point.series.name, ratingParent!.id),
        ].filter((change) => change !== undefined);

        navigate(
            appendScreenFiltersQuery(
                AllPaths.path('ra-risk-log') ?? '',
                clickTrackedChanges,
                screenTrackedChanges,
                includeObsolete,
            ),
        );
    };

    return (
        <PanelFrame
            version={filterVersion}
            title={t('ra:RiskCategories')}
            data-testid="risk-categories-chart"
            panelType={PanelType.GRAPH}
            graphType="STACKEDBAR"
            panelData={chartData}
            showLegend
            hidePanelSetup
            customOptions={{
                colors: stackedChartSeriesColours,
                yAxis: {
                    allowDecimals: false,
                    endOnTick: false,
                },
                plotOptions: {
                    column: {
                        cursor: 'pointer',
                        point: {
                            events: {
                                click: handleClick,
                            },
                        },
                    },
                },
            }}
            customHeight={370}
        />
    );
};

export default memo(RiskDashboardCategories);
